import { useCan, type NavSection } from '@if/mui'
import { access } from '@src/access'
import { useCurrentCompany } from '@src/services/company-hooks'
import { useDevTools } from '@src/types/constants'
import { ArrowLeftRight, Banknote, FileText, History, RefreshCw, Wrench } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * The sidebar nav: one entry per route the app actually serves, so every section is
 * reachable and the current one highlights. The header dropdowns this replaces also
 * listed screens that were never built — those rows navigated nowhere, and they come
 * back with the route that gives them somewhere to go.
 *
 * `/admin/dev-tools` is gated on the same access entry that guards the page itself;
 * `/loans/create` on the dev-tools build flag that declares its route.
 */
const useNavSections = (): NavSection[] => {
  const { t } = useTranslation()
  const canOpenDevTools = useCan(access.openDevTools)
  // On a screen without a company, the links follow the selector.
  const company = useCurrentCompany()

  return useMemo<NavSection[]>(
    () => [
      {
        items: [
          {
            to: `/${company}/loans`,
            label: t('menus.loans.list'),
            icon: FileText,
            // Nested rather than a sibling so the loan detail pages (`/:company/loans/:id`) keep
            // the entry highlighted — a sibling at `/loans/create` would force an exact match.
            children: useDevTools ? [{ to: '/loans/create', label: t('menus.loans.create') }] : undefined,
          },
          { to: `/${company}/disbursements`, label: t('menus.disburse'), icon: Banknote },
        ],
      },
      {
        title: t('menus.collection.collection'),
        items: [
          { to: '/interac-wire', label: t('menus.collection.interacWire'), icon: ArrowLeftRight },
          { to: '/interac-history', label: t('menus.collection.interacHistory'), icon: History },
        ],
      },
      {
        title: t('menus.admin.admin'),
        items: [
          { to: `/${company}/admin/transaction-retry`, label: t('menus.admin.transactionRetry'), icon: RefreshCw },
          ...(canOpenDevTools ? [{ to: '/admin/dev-tools', label: t('menus.admin.devTools'), icon: Wrench }] : []),
        ],
      },
    ],
    [t, canOpenDevTools, company],
  )
}

export default useNavSections
